import './ConfigColumn.scss';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import cn from 'classnames';
import { isQueryArgOnChannel } from '@blisspointmedia/bpm-types/dist/QueryArgConfig';
import { LINEAR_AUDIENCES } from '@blisspointmedia/bpm-types/dist/LinearAudiences';
import { Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { useDrag, useDrop } from 'react-dnd';
import { MdAdd, MdContentCopy, MdDelete, MdEdit, MdMoreVert } from 'react-icons/md';
import {
  Column,
  COMMERCE,
  CROSS_CHANNEL,
  MetricsTablePreset,
  PACING
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { CustomMetric } from '@blisspointmedia/bpm-types/dist/pgTables/CustomMetric';
import {
  DELIVERY_COLS_AND_METRICS,
  MASTER_COLUMN_MAP,
  PERFORMANCE_COLUMN_NAMES
} from '@blisspointmedia/bpm-types/dist/Columns';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import { isKnownMetric, MASTER_METRIC_MAP } from '@blisspointmedia/bpm-types/dist/Metrics';
import { metricDecimalMap } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { refactorEnabled } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { resolveDecimals } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { StreamingColumnCategory } from '@blisspointmedia/bpm-types/dist/Performance';
import * as CC from '@blisspointmedia/bpm-types/dist/CrossChannelPerformance';
import * as CCM from '@blisspointmedia/bpm-types/dist/CrossChannel';
import * as L from '@blisspointmedia/bpm-types/dist/LinearPerformance';
import * as S from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import * as Y from '@blisspointmedia/bpm-types/dist/YoutubePerformance';
import { Appearance, Intent, Placement, Size } from '@Components/designConstants';
import { Button } from '@Components/Button/Button';
import { Dropdown } from '@Components/Dropdown';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import {
  ColumnMetaData,
  ColumnMetaDataMap,
  convertKPIMapToOptions,
  GLOBAL_KPI,
  resolveMinIsBest
} from '@Components/MetricsTable/metrics-table-utils';
import RenameIcon from '@Components/RenameModal/RenameIcon.svg?react';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { roleMap } from '@src/Pages/UserAdmin/UserAdmin';
import { getLagPickerOptions, SingleChannelMediaTypes } from '@src/utils/performance-utils';
import {
  COLUMN_DND_TYPE,
  DeleteColumn,
  InvalidGranularityDetails,
  makeInvalidDimMsg,
  MoveColumn
} from '@src/utils/columnMetricSystem/configUtils';
import { OnBlurTextInput } from '@Components/OnBlurTextInput';
import { GroupedColumnPicker } from '@Pages/CrossChannel/EditTablePresetModal';
import { ColumnTop } from './ColumnTop';

// Channels whose metrics table still uses legacy column names. Streaming is on the column-metric registry, so
// it isn't listed.
const BASIC_COLUMN_TYPES_MAP: Record<string, readonly string[]> = {
  cross_channel: CC.BASIC_COLUMN_TYPES,
  linear: L.BASIC_COLUMN_TYPES,
  youtube: Y.BASIC_COLUMN_TYPES
};

const PERFORMANCE_COLUMN_NAME_SET = new Set(PERFORMANCE_COLUMN_NAMES);
const DELIVERY_BASIC_COLUMN_TYPES: readonly string[] = DELIVERY_COLS_AND_METRICS;

// KPI and lag only apply to columns read from performance. Legacy channels and custom metrics fall back
// to the basic column lists.
const usesKpi = (name: string, prefix: string): boolean => {
  const legacyBasicColumnTypes = BASIC_COLUMN_TYPES_MAP[prefix];
  if (legacyBasicColumnTypes) {
    return !legacyBasicColumnTypes.includes(name);
  }
  if (refactorEnabled(prefix)) {
    if (Object.hasOwn(MASTER_METRIC_MAP, name) && isKnownMetric(name)) {
      return (MASTER_METRIC_MAP[name].requiredColumns ?? []).some(col =>
        PERFORMANCE_COLUMN_NAME_SET.has(col)
      );
    }
    if (Object.hasOwn(MASTER_COLUMN_MAP, name)) {
      return PERFORMANCE_COLUMN_NAME_SET.has(name);
    }
  }
  return !DELIVERY_BASIC_COLUMN_TYPES.includes(name);
};

const LABEL_FIELD_TEXT = 'Label';

type OnColumnChange = <T extends keyof Column>(i: number, key: T, value: Column[T]) => void;

interface ConfigColumnProps {
  addColumn: (i: number) => void;
  addCustomMetric: () => void;
  color?: string;
  column: Column;
  columnMetaDataMap: ColumnMetaDataMap;
  deleteColumn: DeleteColumn;
  deleteCustomMetric: (customMetric: CustomMetric) => void;
  duplicateCustomMetric: (customMetric: CustomMetric) => void;
  editCustomMetric: (customMetric: CustomMetric) => void;
  globalKpi: string;
  // When set, renders two cascading dropdowns instead of the Type dropdown (see GroupedColumnPicker).
  groupedColumnPicker?: GroupedColumnPicker;
  // The company's default window, for the `7d (Default)` label.
  defaultLagWindow?: S.LagOption;
  hasSuperHeader: boolean;
  hoverItem?: string;
  index: number;
  isOnlyColumn: boolean;
  kpiMetaData: Record<string, KpiInfo>;
  moveColumn: MoveColumn;
  onSuperHeaderAdd: () => void;
  prefix: SingleChannelMediaTypes | typeof CROSS_CHANNEL | typeof PACING;
  renameCustomMetric: (customMetric: CustomMetric) => void;
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
  // data-col options that can't be queried alongside the current selection (dims + other data
  // cols), mapped to the pretty names of the selected columns causing the conflict
  invalidGranularityDetails?: InvalidGranularityDetails;
  // data-col options incompatible with the active filter's dimensions
  incompatibleTableDataColsFromFilter?: InvalidGranularityDetails;
}

export const ConfigColumn = ({
  addColumn,
  addCustomMetric,
  color,
  column,
  columnMetaDataMap = {},
  deleteColumn,
  deleteCustomMetric,
  duplicateCustomMetric,
  editCustomMetric,
  globalKpi,
  groupedColumnPicker,
  defaultLagWindow,
  hasSuperHeader,
  hoverItem,
  index,
  isOnlyColumn,
  kpiMetaData = {},
  moveColumn,
  onSuperHeaderAdd,
  prefix,
  renameCustomMetric,
  setPresetChanges,
  invalidGranularityDetails = {},
  incompatibleTableDataColsFromFilter = {}
}: ConfigColumnProps) => {
  const {
    audience,
    dataVarName: dataVarNameExtended,
    decimals,
    divider,
    heatMapping,
    id,
    kpi,
    label,
    lag,
    role,
    source
  } = column;
  const { currency_code: currencyCode } = useCompanyInfo();
  const [selectedCustomMetricActionId, setSelectedCustomMetricActionId] = useState<number>();
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const dataVarName = useMemo(
    () => dataVarNameExtended.replace('Native', '').replace('_native', ''),
    [dataVarNameExtended]
  );

  const kpiOptions = useMemo(
    () => convertKPIMapToOptions(prefix, kpiMetaData),
    [kpiMetaData, prefix]
  );

  const onChange = useCallback<OnColumnChange>(
    (i, key, value) => {
      setPresetChanges(changes => ({
        ...changes,
        dataColumns: changes.dataColumns.map((col, colIndex) =>
          colIndex === i ? { ...col, [key]: value } : col
        )
      }));
    },
    [setPresetChanges]
  );

  // Current grouped-picker selection, derived from the column's dataVarName.
  const groupedSelection = useMemo(
    () => (groupedColumnPicker ? groupedColumnPicker.parse(dataVarNameExtended) : undefined),
    [groupedColumnPicker, dataVarNameExtended]
  );

  // KPI display name in scope (column override, else Global).
  const resolveKpiDisplayName = useCallback(
    (kpiVal?: string): string => {
      const effective = kpiVal || globalKpi;
      return kpiOptions.find(opt => opt.value === effective)?.label ?? effective;
    },
    [kpiOptions, globalKpi]
  );

  // Source display name for an overridden column source; a "Global" column shows none.
  const pickerSourceOptions = groupedColumnPicker?.sourceOptions;
  const resolveSourceDisplayName = useCallback(
    (sourceVal?: string): string | undefined =>
      sourceVal
        ? ((pickerSourceOptions ?? []).find(opt => opt.value === sourceVal)?.label ?? sourceVal)
        : undefined,
    [pickerSourceOptions]
  );

  // Resolve a selection into the column's dataVarName, KPI, source, header label, and decimals.
  const applyGroupedSelection = useCallback(
    (primary: string, secondary: string, kpiVal?: string, sourceVal?: string) => {
      if (!groupedColumnPicker) return;
      const showKpi = groupedColumnPicker.showKpiForPrimary(primary);
      const effectiveKpi = showKpi ? kpiVal : undefined;
      const showSource = groupedColumnPicker.showSourceForPrimary?.(primary) ?? false;
      const effectiveSource = showSource ? sourceVal : undefined;
      const newVar = groupedColumnPicker.toDataVarName(primary, secondary);
      onChange(index, 'dataVarName', newVar);
      onChange(index, 'kpi', effectiveKpi);
      onChange(index, 'source', effectiveSource);
      onChange(
        index,
        'label',
        groupedColumnPicker.headerLabel(
          primary,
          secondary,
          showKpi ? resolveKpiDisplayName(effectiveKpi) : undefined,
          showSource ? resolveSourceDisplayName(effectiveSource) : undefined
        )
      );
      const meta = columnMetaDataMap[newVar];
      if (meta?.decimals !== undefined) {
        onChange(index, 'decimals', meta.decimals);
      } else if (metricDecimalMap[newVar]) {
        onChange(index, 'decimals', metricDecimalMap[newVar]);
      } else {
        onChange(index, 'decimals', undefined);
      }
    },
    [
      groupedColumnPicker,
      index,
      onChange,
      columnMetaDataMap,
      resolveKpiDisplayName,
      resolveSourceDisplayName
    ]
  );

  // A custom metric whose formula hardcodes a specific KPI for every `volume`/`revenue` reference
  // (i.e. no reference is left as GLOBAL_KPI) has nothing for the column-level KPI override to
  // affect, so the KPI/Lag/Audience pickers are hidden for it — and any stale override values are
  // cleared so they don't silently persist unused.
  const customMetricHasAllHardcodedKPIs = useMemo(() => {
    const metadata =
      columnMetaDataMap[dataVarName] ?? columnMetaDataMap[Object.keys(columnMetaDataMap)[0]];
    if (!metadata?.isCustom) return false;
    const hasAllHardcodedKPIs = !(metadata.customMetaData?.kpis ?? []).some(
      kpiCol => kpiCol.kpi === GLOBAL_KPI
    );
    if (hasAllHardcodedKPIs) {
      onChange(index, 'kpi', undefined);
      onChange(index, 'lag', undefined);
      onChange(index, 'audience', undefined);
    }
    return hasAllHardcodedKPIs;
  }, [columnMetaDataMap, dataVarName, index, onChange]);

  const [isSpecial, isComputedCurrencyCol, metadata, category] = useMemo(() => {
    const baseMeta =
      columnMetaDataMap[dataVarName] ?? columnMetaDataMap[Object.keys(columnMetaDataMap)[0]];
    const special = usesKpi(dataVarName, prefix);
    const computedCurrencyCol = (CC.COMPUTED_CURRENCY_DATA_TYPES as readonly string[]).includes(
      dataVarName
    );
    const kpiInfo = special ? kpiMetaData[kpi || globalKpi] : undefined;
    const meta =
      special && baseMeta && kpiInfo
        ? {
            ...baseMeta,
            decimals: kpiInfo.decimals,
            prettyName: baseMeta.prettyName?.replace('CPX', kpiInfo.cpxName)
          }
        : baseMeta;
    return [special, computedCurrencyCol, meta, meta?.category] as const;
  }, [columnMetaDataMap, dataVarName, prefix, kpiMetaData, kpi, globalKpi]);

  const [{ isDragging }, drag, preview] = useDrag<
    { type: typeof COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isDragging: boolean }
  >({
    type: COLUMN_DND_TYPE,
    item: { type: COLUMN_DND_TYPE, id, index },
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  const [{ isOver, dropIndex }, drop] = useDrop<
    { type: typeof COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isOver: boolean; dropIndex: number | undefined }
  >({
    accept: COLUMN_DND_TYPE,
    canDrop: item => item.id !== id,
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      dropIndex: monitor.getItem()?.index
    }),
    drop: item => moveColumn(item.id, column.id)
  });

  const columnDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      preview(node);
      drop(node);
    },
    [preview, drop]
  );
  const handleDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      drag(node);
    },
    [drag]
  );

  const defaultLabel = useMemo(() => {
    if (dataVarName.startsWith('avgRevenue')) {
      return `${kpiMetaData[kpi || globalKpi]?.revenueHeader || 'Avg. Rev.'} (default)`;
    }
    return `${metadata?.prettyName ?? dataVarName} (default)`;
  }, [dataVarName, metadata, kpiMetaData, kpi, globalKpi]);

  const [categorySelectVal, categorySelectOptions] = useMemo(() => {
    const categoryOptions = Object.values(StreamingColumnCategory).map(cat => ({
      label: cat,
      value: cat
    }));
    return [
      category ? { value: category, label: category } : null,
      [...categoryOptions].sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      )
    ];
  }, [category]);

  const renderCustomMetricActions = useCallback(
    (customMetric: CustomMetric) => {
      const runAndClose = (action: (customMetric: CustomMetric) => void) => () => {
        setSelectedCustomMetricActionId(undefined);
        action(customMetric);
      };
      return (
        <OverlayTrigger
          show={selectedCustomMetricActionId === customMetric.id}
          placement={Placement.RIGHT_START}
          overlay={
            <Popover id={`custom-metric-${customMetric.id}-actions-popover`}>
              <div className='custom-metric-actions'>
                <InteractiveDiv
                  className='custom-metric-action'
                  onClick={runAndClose(editCustomMetric)}
                >
                  <div className='custom-metric-action-icon'>
                    <MdEdit />
                  </div>
                  <div className='custom-metric-action-text'>Edit</div>
                </InteractiveDiv>
                <InteractiveDiv
                  className='custom-metric-action'
                  onClick={runAndClose(renameCustomMetric)}
                >
                  <div className='custom-metric-action-icon'>
                    <RenameIcon />
                  </div>
                  <div className='custom-metric-action-text'>Rename</div>
                </InteractiveDiv>
                <InteractiveDiv
                  className='custom-metric-action'
                  onClick={runAndClose(deleteCustomMetric)}
                >
                  <div className='custom-metric-action-icon'>
                    <MdDelete />
                  </div>
                  <div className='custom-metric-action-text'>Delete</div>
                </InteractiveDiv>
                <InteractiveDiv
                  className='custom-metric-action'
                  onClick={runAndClose(duplicateCustomMetric)}
                >
                  <div className='custom-metric-action-icon'>
                    <MdContentCopy />
                  </div>
                  <div className='custom-metric-action-text'>Duplicate</div>
                </InteractiveDiv>
                <div className='custom-metric-last-modified-container'>
                  <div className='custom-metric-last-modified-text'>Last updated:</div>
                  <div className='custom-metric-last-modified-text'>
                    {customMetric.last_modified} by {customMetric.last_user}
                  </div>
                </div>
              </div>
            </Popover>
          }
        >
          <IconButton
            className='custom-metric-actions-expander'
            appearance={Appearance.EMPTY}
            onClick={() =>
              setSelectedCustomMetricActionId(
                selectedCustomMetricActionId === customMetric.id ? undefined : customMetric.id
              )
            }
            tooltip={{ text: 'More actions' }}
          >
            <MdMoreVert />
          </IconButton>
        </OverlayTrigger>
      );
    },
    [
      deleteCustomMetric,
      duplicateCustomMetric,
      editCustomMetric,
      renameCustomMetric,
      selectedCustomMetricActionId
    ]
  );

  const getValidTypeOptions = useCallback(
    (categoryOverride: string | null = null) => {
      const columnMetaDataMapFiltered: ColumnMetaDataMap = {};
      for (const key of Object.keys(columnMetaDataMap)) {
        // Native metrics are selected by currency type dropdown
        if ((CCM.NATIVE_CURRENCY_COLUMN_TYPES as readonly string[]).includes(key)) {
          continue;
        }
        const col = columnMetaDataMap[key] as ColumnMetaData;
        const categoryToFilterBy = categoryOverride ?? categorySelectVal?.value;
        if (!categoryToFilterBy || col.category === categoryToFilterBy) {
          columnMetaDataMapFiltered[key] = columnMetaDataMap[key];
        }
        if (key === 'impressions' || key === 'imps') {
          // We still want the default impressions to show thousands in the label for consistency
          // but now there is the option to switch to whole number in the config
          columnMetaDataMapFiltered[key] = {
            ...columnMetaDataMapFiltered[key],
            prettyName: 'Impressions'
          } as ColumnMetaData;
        }
      }
      const typeOptions: Record<string, MenuItem<string> & { label: string }> = {};
      for (const [type, val] of Object.entries(columnMetaDataMapFiltered)) {
        if (!val) continue;
        const peerBlockers = invalidGranularityDetails[type];
        const filterBlockers = incompatibleTableDataColsFromFilter[type];
        const isInvalidGranularity = Boolean(peerBlockers) || Boolean(filterBlockers);
        typeOptions[type] = {
          className: cn({
            'dropdown-open':
              val.customMetaData && selectedCustomMetricActionId === val.customMetaData.id
          }),
          label: val.prettyName || type,
          value: type,
          disabled: isInvalidGranularity,
          simpleTooltip: isInvalidGranularity
            ? makeInvalidDimMsg({
                prettyDims: [val.prettyName || type],
                isInvalidAnalyticsDim: false,
                conflictingTableDims: peerBlockers,
                conflictingFilterDims: filterBlockers
              })
            : undefined,
          simpleTooltipVerticalShift: '-55px',
          trailingContent: val.isCustom ? (
            <div className='custom-metric-label'>custom</div>
          ) : undefined,
          itemOptions: val.customMetaData
            ? renderCustomMetricActions(val.customMetaData)
            : undefined
        };
      }
      return typeOptions;
    },
    [
      categorySelectVal,
      columnMetaDataMap,
      invalidGranularityDetails,
      incompatibleTableDataColsFromFilter,
      renderCustomMetricActions,
      selectedCustomMetricActionId
    ]
  );

  const [typeSelectVal, typeSelectOptions] = useMemo(() => {
    const typeOptions = getValidTypeOptions();
    const selectedVar = typeOptions[dataVarName] ? dataVarName : Object.keys(typeOptions)[0];
    const sorted = Object.values(typeOptions).sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
    return [{ value: selectedVar, label: typeOptions[selectedVar]?.label ?? selectedVar }, sorted];
  }, [getValidTypeOptions, dataVarName]);

  useEffect(() => {
    if (typeSelectVal.value && typeSelectVal.value !== dataVarName) {
      onChange(index, 'dataVarName', typeSelectVal.value);
    }
  }, [typeSelectVal, dataVarName, index, onChange]);

  const addNewCustomMetricButton = (
    <div className='add-new-custom-metric-button'>
      <MdAdd /> Add New Custom Metric
    </div>
  );

  const renderKpiSourcePicker = () => {
    if (groupedColumnPicker) {
      if (!groupedSelection) return null;
      return (
        <>
          {groupedColumnPicker.showKpiForPrimary(groupedSelection.primary) && (
            <div className='basic-section kpi-section'>
              <Dropdown
                size={Size.MD}
                label='KPI'
                onChange={val =>
                  applyGroupedSelection(
                    groupedSelection.primary,
                    groupedSelection.secondary,
                    val === 'Global' ? undefined : val,
                    source
                  )
                }
                options={[{ label: 'Global', value: 'Global' }, ...kpiOptions]}
                value={kpi ?? 'Global'}
              />
            </div>
          )}
          {(groupedColumnPicker.showSourceForPrimary?.(groupedSelection.primary) ?? false) && (
            <div className='basic-section kpi-section'>
              <Dropdown
                size={Size.MD}
                label='Source'
                onChange={val =>
                  applyGroupedSelection(
                    groupedSelection.primary,
                    groupedSelection.secondary,
                    kpi,
                    val === 'Global' ? undefined : val
                  )
                }
                options={[
                  { label: 'Global', value: 'Global' },
                  ...(groupedColumnPicker.sourceOptions ?? [])
                ]}
                value={source ?? 'Global'}
              />
            </div>
          )}
        </>
      );
    }
    if (isSpecial) {
      return (
        <>
          <div className='basic-section kpi-section'>
            {!customMetricHasAllHardcodedKPIs && (
              <Dropdown
                size={Size.MD}
                label='KPI'
                onChange={val => onChange(index, 'kpi', val === 'Global' ? undefined : val)}
                options={[{ label: 'Global', value: 'Global' }, ...kpiOptions]}
                value={kpi ?? 'Global'}
              />
            )}
          </div>
          <div className='basic-section'>
            {isQueryArgOnChannel('lag', prefix) && (
              <Dropdown
                size={Size.MD}
                label='Lag'
                onChange={val =>
                  onChange(index, 'lag', val === 'Global' ? undefined : (val as Column['lag']))
                }
                options={[
                  { label: 'Global', value: 'Global' },
                  ...getLagPickerOptions(defaultLagWindow)
                ]}
                value={lag || 'Global'}
              />
            )}
            {isQueryArgOnChannel('audience', prefix) && (
              <Dropdown
                size={Size.MD}
                label='Audience'
                onChange={val =>
                  onChange(
                    index,
                    'audience',
                    val === 'Global' ? undefined : (val as Column['audience'])
                  )
                }
                options={['Global', ...LINEAR_AUDIENCES]}
                value={audience || 'Global'}
              />
            )}
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <div className='column-config-outer' ref={columnDropRef}>
      <div
        className={cn('column-config', {
          'has-divider': divider,
          'is-over': isOver,
          left: isOver && (dropIndex ?? -1) > index,
          right: isOver && (dropIndex ?? -1) <= index,
          dragging: isDragging,
          'is-hover-item': hoverItem === id
        })}
      >
        <ColumnTop
          addColumn={addColumn}
          color={color}
          handleRef={handleDragRef}
          hasSuperHeader={hasSuperHeader}
          onSuperHeaderAdd={onSuperHeaderAdd}
          i={index}
        />
        {category && (
          <div className='form-section'>
            <Dropdown
              size={Size.MD}
              label='Category'
              options={categorySelectOptions}
              value={categorySelectVal?.value}
              onChange={val => {
                const newTypeOptions = getValidTypeOptions(val);
                if (!Object.keys(newTypeOptions).includes(dataVarName)) {
                  onChange(index, 'dataVarName', Object.keys(newTypeOptions)[0]);
                }
              }}
            />
          </div>
        )}
        {groupedColumnPicker && groupedSelection ? (
          <>
            <div className='form-section'>
              <Dropdown
                size={Size.MD}
                label={groupedColumnPicker.primaryLabel}
                options={groupedColumnPicker.primaryOptions}
                value={groupedSelection.primary}
                onChange={val =>
                  applyGroupedSelection(val, groupedSelection.secondary, kpi, source)
                }
              />
            </div>
            <div className='form-section'>
              <Dropdown
                size={Size.MD}
                label={groupedColumnPicker.secondaryLabel}
                options={groupedColumnPicker.secondaryOptions}
                value={groupedSelection.secondary}
                onChange={val => applyGroupedSelection(groupedSelection.primary, val, kpi, source)}
              />
            </div>
          </>
        ) : (
          <div className='form-section'>
            <Dropdown
              size={Size.MD}
              className='column-type-dropdown'
              label='Type'
              show={showTypeDropdown}
              hasTicks={false}
              onToggle={nextShow => {
                setShowTypeDropdown(nextShow);
                if (selectedCustomMetricActionId !== undefined) {
                  setSelectedCustomMetricActionId(undefined);
                }
              }}
              footer={addNewCustomMetricButton}
              footerOnClick={addCustomMetric}
              options={typeSelectOptions}
              value={typeSelectVal.value}
              onChange={val => {
                setShowTypeDropdown(false);
                onChange(index, 'dataVarName', val);
                onChange(index, 'label', undefined);
                const meta = columnMetaDataMap[val];
                if (meta?.isCustom) {
                  onChange(
                    index,
                    'requiredColumns',
                    meta.requiredColumns ? [...meta.requiredColumns] : undefined
                  );
                  onChange(index, 'customColumnID', meta.customMetaData?.id);
                } else {
                  if (!usesKpi(val, prefix)) {
                    onChange(index, 'kpi', undefined);
                    onChange(index, 'lag', undefined);
                    onChange(index, 'audience', undefined);
                  }
                  onChange(index, 'requiredColumns', undefined);
                }
                if (meta?.decimals !== undefined) {
                  onChange(index, 'decimals', meta.decimals);
                } else if (metricDecimalMap[dataVarName]) {
                  onChange(index, 'decimals', metricDecimalMap[dataVarName]);
                } else {
                  onChange(index, 'decimals', undefined);
                }
              }}
            />
          </div>
        )}
        <div className='form-section'>
          <div className='label'>{LABEL_FIELD_TEXT}</div>
          <OnBlurTextInput
            size='sm'
            value={label || ''}
            placeholder={defaultLabel}
            onChange={val => onChange(index, 'label', val || undefined)}
          />
        </div>
        {renderKpiSourcePicker()}
        {typeSelectVal.value === 'impressions' || typeSelectVal.value === 'imps' ? (
          <div className='form-section'>
            <div className='label'>Format:</div>
            <Dropdown
              size={Size.MD}
              value={(decimals ?? metadata?.decimals) === -1 ? 'raw' : 'thousands'}
              options={[
                { value: 'thousands', label: 'View in thousands' },
                { value: 'raw', label: 'View whole number ' }
              ]}
              onChange={val => {
                const labelToUse = val === 'thousands' ? 'Impressions (000s)' : 'Impressions';
                onChange(index, 'label', labelToUse);
                onChange(index, 'decimals', val === 'thousands' ? 0 : -1);
              }}
            />
          </div>
        ) : (
          <div className='form-section'>
            <div className='label'>Decimals:</div>
            <OnBlurTextInput
              size='sm'
              type='number'
              min='0'
              value={`${decimals ?? metadata?.decimals ?? ''}`}
              onChange={val => {
                const parsed = parseInt(val, 10);
                onChange(
                  index,
                  'decimals',
                  Number.isNaN(parsed) || parsed < 0 ? undefined : resolveDecimals(parsed)
                );
              }}
              placeholder={`${metadata?.decimals ?? metricDecimalMap[dataVarName] ?? 0} (default)`}
            />
          </div>
        )}
        {isComputedCurrencyCol && prefix !== COMMERCE && (
          <div className='form-section'>
            <div className='label currency-type'>
              <span>Currency Type:</span>
              <InfoTooltip size={Size.SM}>
                Select how currency values are displayed for this metric.
                <ul>
                  <li>
                    {`Normalized to ${currencyCode}`} (Default):{' '}
                    <span>{`Converts foreign currency values to ${currencyCode} using daily exchange rates.`}</span>
                  </li>
                  <li>
                    Raw:{' '}
                    <span>
                      Shows values as reported by the platform in their original currency.
                    </span>
                  </li>
                </ul>
                Important:{' '}
                <span>When we do not know a platform’s raw currency we assume it is USD.</span>
              </InfoTooltip>
            </div>
            <Dropdown
              size={Size.MD}
              onChange={val => {
                const metric = CCM.NORMAL_NATIVE_METRIC_MAP[typeSelectVal.value]?.[val];
                onChange(index, 'dataVarName', metric as Column['dataVarName']);
                onChange(index, 'requiredColumns', undefined);
              }}
              options={[
                { label: `Normalized to ${currencyCode}`, value: 'normalized' },
                { label: 'Raw Value', value: 'native' }
              ]}
              value={
                (CCM.NATIVE_CURRENCY_COLUMN_TYPES as readonly string[]).includes(
                  dataVarNameExtended
                )
                  ? 'native'
                  : 'normalized'
              }
            />
          </div>
        )}
        {index > 0 && (
          <div className='basic-section checkbox-section'>
            <div className='label'>Divider?:</div>
            <Form.Check
              checked={Boolean(divider)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(index, 'divider', e.target.checked)
              }
            />
          </div>
        )}
        <div className='basic-section'>
          <Dropdown
            size={Size.MD}
            label='Role'
            value={`${role ?? 10}`}
            options={Object.keys(roleMap).map(roleKey => ({
              label: roleMap[Number(roleKey)],
              value: roleKey
            }))}
            onChange={val => onChange(index, 'role', parseInt(val, 10))}
          />
        </div>
        <div className='basic-section checkbox-section'>
          <div className='label'>Heat Mapping?:</div>
          <Form.Check
            checked={Boolean(heatMapping)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(
                index,
                'heatMapping',
                e.target.checked ? { colorScheme: 'diverging' } : undefined
              )
            }
          />
        </div>
        {heatMapping && (
          <div className='heatmapping-section'>
            <div className='header-section'>
              <div className='label'>Heat Mapping</div>
              <OverlayTrigger
                placement={Placement.TOP}
                overlay={
                  <Popover
                    id={`${id}-heatmap-glossary-popover`}
                    className='performance-heatmap-glossary-popover'
                  >
                    <Popover.Header>Glossary</Popover.Header>
                    <Popover.Body>
                      <div className='glossary-item'>
                        <span>Exclude Zeros:</span>
                        <span>
                          Ignores zeros when calculating. They aren&apos;t factored into means,
                          medians, or std. dev. calculations, nor are they considered minimum or
                          maximum values.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Pivot:</span>
                        <span>
                          Point at which colors change. Does not apply to &quot;Green&quot; color
                          scheme.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Mid Point:</span>
                        <span>
                          Half way between the maximum and minimum values (respects Std. Dev.
                          Clipping).
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Color:</span>
                        <span>
                          Color scheme. &quot;Standard&quot; goes from red to yellow, then yellow to
                          green (yellow being at the pivot). &quot;Green&quot; goes from white to
                          green.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Std. Dev. Clipping:</span>
                        <span>
                          Numbers that are greater or less than this number of standard deviations
                          from the mean (not pivot) will not be considered when heat mapping. This
                          is to prevent outliers from impacting the heat mapping.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Minimum/Maximum:</span>
                        <span>
                          Like &quot;Std. Dev. Clipping&quot;, but hard-coded. Numbers below the
                          minimum and above the maximum are treated as if they are those numbers. If
                          you put a maximum of 100, a value of 500 is treated as 100 for heat
                          mapping calculations.
                        </span>
                      </div>
                    </Popover.Body>
                  </Popover>
                }
              >
                <span className='glossary'>(Glossary)</span>
              </OverlayTrigger>
            </div>
            {/* NOTE: bpm gated this checkbox on `metadata.contentReplacement?.threshold === 0`; that
                field doesn't exist on tinuiti's ColumnMetaData yet, so it's always shown here (a minor,
                flagged simplification — matches the already-migrated CrossChannel equivalent). */}
            <div className='basic-section checkbox-section'>
              <div className='label'>Exclude Zeros</div>
              <Form.Check
                checked={Boolean(heatMapping.excludeZeros)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(index, 'heatMapping', { ...heatMapping, excludeZeros: e.target.checked })
                }
              />
            </div>
            <div className='basic-section'>
              <Dropdown
                size={Size.MD}
                label='Pivot'
                onChange={val => onChange(index, 'heatMapping', { ...heatMapping, midpoint: val })}
                options={['MEDIAN', 'MIDPOINT', 'MEAN', 'CUSTOM']}
                value={heatMapping.midpoint || 'MIDPOINT'}
              />
            </div>
            {heatMapping.midpoint === 'CUSTOM' && (
              <div className='form-section'>
                <div className='label'>Custom Pivot:</div>
                <OnBlurTextInput
                  size='sm'
                  type='number'
                  value={`${heatMapping.customMidpoint ?? ''}`}
                  onChange={val => {
                    const parsed = parseFloat(val);
                    const { customMidpoint: _customMidpoint, ...restHeatMapping } = heatMapping;
                    onChange(index, 'heatMapping', {
                      ...restHeatMapping,
                      customMidpoint: Number.isNaN(parsed) ? undefined : parsed
                    });
                  }}
                  placeholder='Custom pivot'
                />
              </div>
            )}
            <div className='basic-section'>
              <Dropdown
                size={Size.MD}
                label='Color'
                value={heatMapping.colorScheme || 'diverging'}
                options={[
                  { label: 'Diverging', value: 'diverging' },
                  { label: 'Sequential', value: 'sequential' }
                ]}
                onChange={val =>
                  onChange(index, 'heatMapping', { ...heatMapping, colorScheme: val })
                }
              />
            </div>
            <div className='basic-section checkbox-section'>
              <div className='label'>Min Is Best?:</div>
              <Form.Check
                checked={resolveMinIsBest(dataVarName, heatMapping, metadata)}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const { minIsBest: _minIsBest, ...restHeatMapping } = heatMapping;
                  onChange(index, 'heatMapping', {
                    ...restHeatMapping,
                    minIsBest: e.target.checked
                  });
                }}
              />
            </div>
            <div className='form-section'>
              <div className='label'>Std. Dev. Clipping:</div>
              <OnBlurTextInput
                size='sm'
                type='number'
                value={`${heatMapping.stdevClipping ?? ''}`}
                onChange={val => {
                  const parsed = parseFloat(val);
                  const { stdevClipping: _stdevClipping, ...restHeatMapping } = heatMapping;
                  onChange(index, 'heatMapping', {
                    ...restHeatMapping,
                    stdevClipping: Number.isNaN(parsed) ? undefined : parsed
                  });
                }}
                placeholder='None (default)'
              />
            </div>
            <div className='form-section'>
              <div className='label'>Minimum:</div>
              <OnBlurTextInput
                size='sm'
                type='number'
                value={`${heatMapping.min ?? ''}`}
                onChange={val => {
                  const parsed = parseFloat(val);
                  const { min: _min, ...restHeatMapping } = heatMapping;
                  onChange(index, 'heatMapping', {
                    ...restHeatMapping,
                    min: Number.isNaN(parsed) ? undefined : parsed
                  });
                }}
                placeholder='None (default)'
              />
            </div>
            <div className='form-section'>
              <div className='label'>Maximum:</div>
              <OnBlurTextInput
                size='sm'
                type='number'
                value={`${heatMapping.max ?? ''}`}
                onChange={val => {
                  const parsed = parseFloat(val);
                  const { max: _max, ...restHeatMapping } = heatMapping;
                  onChange(index, 'heatMapping', {
                    ...restHeatMapping,
                    max: Number.isNaN(parsed) ? undefined : parsed
                  });
                }}
                placeholder='None (default)'
              />
            </div>
          </div>
        )}
        {!isOnlyColumn && (
          <Button
            size={Size.MD}
            appearance={Appearance.OUTLINED}
            design={Intent.ERROR}
            className='delete-button'
            onClick={() => deleteColumn(index)}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};
